import type { ElementType } from 'react';
import {
  InfoOutlined as Info,
  LocalPhoneOutlined as Phone,
  CollectionsBookmarkOutlined,
  Business,
  ContactsOutlined,
  ViewQuiltOutlined,
  ArchiveOutlined,
  LocalParking,
  DirectionsCar,
  SettingsOutlined,
  EventNoteOutlined,
  MiscellaneousServices,
  ShoppingCartOutlined,
  RateReviewOutlined,
  SecurityOutlined,
  ContactMail,
  CenterFocusStrongOutlined,
  ReceiptLongOutlined,
  Face,
  FolderOutlined,
  AssessmentOutlined,
} from '@mui/icons-material';
import { ROUTES } from '@consts/routes';
import i18n from '@shared/config/i18n';

const { t } = i18n;
const buildType = process.env.BUILD_TYPE;

type MenuIcon = ElementType<{ className?: string }> | string;

export type NavigationMenuItem = {
  link?: string;
  text: string;
  icon?: MenuIcon;
  activeIcon?: MenuIcon;
  nest?: boolean;
  items?: NavigationMenuItem[];
  display_text?: string;
};

export type NavigationMenuConfig = {
  items: NavigationMenuItem[];
};

export const menuConfig: NavigationMenuConfig = {
  items: [
    {
      link: ROUTES.PROFILE,
      text: t('navMenu.profile'),
      icon: Face,
    },
    /* MANAGEMENT */
    {
      text: t('navMenu.directories.title'),
      icon: CollectionsBookmarkOutlined,
      nest: true,
      items: [
        {
          link: ROUTES.OBJECTS_NEW,
          text: t('navMenu.directories.objects-new'),
        },
        {
          link: ROUTES.ENTERPRISE_LIST,
          text: t('navMenu.directories.enterprises-new'),
        },
        {
          link: ROUTES.ENTERPRISES,
          text: t('navMenu.directories.enterprises'),
        },
        {
          link: ROUTES.RESIDENTS_NEW,
          text: t('navMenu.directories.residents'),
        },
        {
          link: ROUTES.PEOPLE,
          text: t('navMenu.directories.people'),
        },
        {
          link: ROUTES.USERS,
          text: t('navMenu.directories.users'),
        },
        {
          link: ROUTES.VEHICLES,
          text: t('navMenu.directories.vehicles'),
        },
        {
          link: ROUTES.EQUIPMENT,
          text: t('navMenu.directories.equipment'),
        },
        {
          link: ROUTES.CLIENT_PREMISES,
          text: t('navMenu.settings.clientPremises'),
        },
        {
          link: ROUTES.PARKING,
          text: t('navMenu.directories.parking'),
        },
        {
          link: ROUTES.ENTERPRISE_CONTRACTOR,
          text: t('navMenu.directories.enterpriseContractor'),
        },
      ],
    },
    {
      text: t('navMenu.dispatch.title'),
      icon: EventNoteOutlined,
      nest: true,
      items: [
        {
          link: ROUTES.TICKETS,
          text: t('navMenu.dispatch.tickets'),
        },
        {
          link: ROUTES.INSPECTIONS,
          text: t('navMenu.settings.Inspections'),
        },
        {
          link: ROUTES.CALLS,
          text: t('navMenu.dispatch.calls'),
        },
        {
          link: ROUTES.REPORTS,
          text: t('navMenu.reports.reportsTicket'),
        },
        {
          link: ROUTES.APPOINTMENT,
          text: t('navMenu.informationCenter.appointment'),
        },
        {
          text: t('navMenu.apartmentAcceptance'),
          link: ROUTES.APARTMENT_ACCEPTANCE,
        },
        {
          link: ROUTES.TICKETS_TYPES,
          text: t('navMenu.settings.ticketsTypes'),
        },
        {
          link: ROUTES.STATISTICS,
          text: t('navMenu.statistics.title'),
        },
        {
          link: ROUTES.TBO_SCHEDULE,
          text: t('navMenu.dispatch.tboSchedule'),
        },
        {
          link: ROUTES.JOB_SCHEDULE,
          text: t('navMenu.dispatch.jobSchedule'),
        },
        {
          link: ROUTES.PLANNED_WORK_SCHEDULE,
          text: t('navMenu.dispatch.SchedulePPR'),
        },
        {
          link: ROUTES.EMPLOYEE_ASSIGNMENT,
          text: t('navMenu.settings.employeeAssignment'),
        },
        {
          link: ROUTES.PLANNED_WORKS,
          text: t('navMenu.settings.ScheduledMaintenance'),
        },
        {
          link: ROUTES.QUALIFICATIONS,
          text: t('navMenu.settings.Qualifications'),
        },
        {
          link: ROUTES.WORKS,
          text: t('navMenu.settings.works'),
        },
        {
          link: ROUTES.WORK_FLOWS,
          text: t('navMenu.settings.workFlows'),
        },
        {
          link: ROUTES.CHECK_LISTS,
          text: t('navMenu.settings.CheckLists'),
        },
        {
          link: ROUTES.APPEALS,
          text: t('navMenu.dispatch.Appeals'),
        },
      ],
    },
    {
      text: t('Monitoring'),
      icon: CenterFocusStrongOutlined,
      nest: true,
      items: [
        {
          link: ROUTES.DASHBOARD,
          text: t('navMenu.dashboard'),
        },
        {
          link: ROUTES.DASHBOARD_CLIMATE,
          text: t('MicroclimateDashboard'),
        },
        {
          link: ROUTES.TICKETS_DISPLAY,
          text: t('navMenu.dispatch.tickets-display'),
        },
        {
          link: ROUTES.SECURITY,
          text: t('navMenu.security.security'),
        },
        {
          link: ROUTES.CONCIERGE,
          text: t('navMenu.dispatch.concierge'),
        },
        {
          link: ROUTES.ALARMS,
          text: t('navMenu.engineeringSystems.alarms'),
        },
      ],
    },
    {
      text: t('navMenu.mutualSettlements.title'),
      icon: ReceiptLongOutlined,
      nest: true,
      items: [
        {
          link: ROUTES.ACCOUNTS,
          text: t('navMenu.directories.personalAccounts'),
        },
        {
          link: ROUTES.ACCOUNTS_NEW,
          text: t('navMenu.directories.personalAccounts'),
        },
        {
          link: ROUTES.DEBTORS_NOTIFICATIONS,
          text: t('navMenu.debtors.Notifications'),
        },
        {
          link: ROUTES.DEBTORS_SETTINGS,
          text: t('navMenu.debtors.settings'),
        },
        {
          link: ROUTES.COUNTERS_CHECK,
          text: t('navMenu.engineeringSystems.ReconciliationOfMeteringDevices'),
        },
        {
          link: ROUTES.DEBTORS_LIST,
          text: t('navMenu.debtors.list'),
        },
        {
          link: ROUTES.DEBTORS_RECEIPTS,
          text: t('navMenu.debtors.archiveReceipts'),
        },
        {
          link: ROUTES.DEBTORS_ACCESS,
          text: t('navMenu.debtors.Restrictions'),
        },
      ],
    },
    {
      text: t('navMenu.security.title'),
      icon: SecurityOutlined,
      nest: true,
      items: [
        {
          link: ROUTES.INTERCOMS,
          text: t('navMenu.security.intercoms'),
        },
        {
          link: ROUTES.CCTV,
          text: t('navMenu.security.cctv'),
        },
        {
          link: ROUTES.BARRIERS,
          text: t('navMenu.security.barriers'),
        },
        {
          link: ROUTES.BARRIERS_NEW,
          text: `${t('navMenu.security.BarriersAkado')}`,
        },
        {
          link: ROUTES.PASS_CONFIG,
          text: t('navMenu.settings.PassConfigurator'),
        },
        {
          link: ROUTES.ACCESS_KEYS,
          text: t('navMenu.security.accessKeys'),
        },
        {
          link: ROUTES.ACCESS_GROUPS,
          text: t('navMenu.security.accessGroups'),
        },
        {
          link: ROUTES.USERS_GROUPS,
          text: t('GroupsOfUsers'),
        },
        {
          link: ROUTES.ACCESS_LEVEL,
          text: t('AccessLevel'),
        },
        {
          link: ROUTES.SCHEDULE,
          text: t('ScheduleTitle'),
        },
        {
          link: ROUTES.GUESTS,
          text: t('navMenu.security.guests'),
        },
        {
          link: ROUTES.GUESTS_TYPES,
          text: t('TypesOfGuests'),
        },
        {
          link: ROUTES.PASSES,
          text:
            buildType === 'stone'
              ? t('navMenu.enterprises-passes')
              : t('navMenu.security.passes'),
        },
        {
          link: ROUTES.EVENTS_LOG,
          text: t('navMenu.security.eventsLog'),
          display_text: t('navMenu.security.eventsLogDisplayText'),
        },
        {
          link: ROUTES.STUFF,
          text: t('navMenu.security.stuff'),
        },
        {
          link: ROUTES.RENT,
          text: t('Booking'),
        },
        {
          link: ROUTES.QUARANTINE,
          text: t('navMenu.security.quarantine'),
        },
      ],
    },
    {
      text: t('navMenu.informationCenter.title'),
      icon: Info,
      nest: true,
      items: [
        {
          link: ROUTES.NEWS,
          text: t('navMenu.informationCenter.news'),
        },
        {
          link: ROUTES.STORIES,
          text: t('navMenu.informationCenter.stories'),
        },
        {
          link: ROUTES.MESSAGES,
          text: t('navMenu.informationCenter.CommunicationWithResidents'),
        },
        {
          text: t('navMenu.informationCenter.polls'),
          link: ROUTES.POLLS,
        },
      ],
    },
    {
      link: ROUTES.ANALYTICS,
      text: t('navMenu.analytics'),
      icon: AssessmentOutlined,
    },
    {
      text: t('navMenu.engineeringSystems.title'),
      icon: MiscellaneousServices,
      nest: true,
      items: [
        {
          link: ROUTES.COUNTERS,
          text: t('navMenu.engineeringSystems.counters'),
        },
        {
          link: ROUTES.DOORS,
          text: t('navMenu.security.doors'),
        },
        {
          link: ROUTES.CONTAINERS,
          text: t('navMenu.engineeringSystems.containers'),
        },
        {
          link: ROUTES.EVENT_SERVICE,
          text: t('navMenu.settings.eventService'),
        },
        {
          link: ROUTES.DEVICES_HINTS,
          text: t('navMenu.settings.devicesHints'),
        },
      ],
    },
    {
      text: t('navMenu.market.title'),
      icon: ShoppingCartOutlined,
      nest: true,
      items: [
        {
          link: ROUTES.MARKET_COMPANIES,
          text: t('navMenu.market.marketCompanies'),
        },
        {
          link: ROUTES.MARKET_OFFERS,
          text: t('navMenu.market.marketOffers'),
        },
      ],
    },
    {
      link: ROUTES.DOCUMENT_FLOW,
      text: t('navMenu.documents'),
      icon: FolderOutlined,
    },
    {
      text: t('navMenu.settings.title'),
      nest: true,
      icon: SettingsOutlined,
      items: [
        {
          link: ROUTES.COMPANY,
          text: t('navMenu.settings.company'),
        },
        {
          link: ROUTES.CONNECTIONS,
          text: t('navMenu.dispatch.connections'),
        },
        {
          link: ROUTES.USER_APP,
          text: t('navMenu.settings.userApp'),
        },
        {
          link: ROUTES.SMS_GATEWAY_SETTINGS,
          text: t('navMenu.settings.smsGatewaySettings'),
        },
        {
          link: ROUTES.ROLES,
          text: t('navMenu.settings.roles'),
        },
        {
          link: ROUTES.ACTIVITY,
          text: t('navMenu.directories.activity'),
        },
        {
          link: ROUTES.PARKING_LOTS,
          text: t('navMenu.settings.parkingLots'),
        },
        {
          link: ROUTES.TICKETS_FIELDS,
          text: t('navMenu.fields-constructor'),
        },
        {
          link: ROUTES.TICKETS_SCHEMAS,
          text: t('navMenu.forms-constructor'),
        },
      ],
    },
    /* ENTERPRISES */
    {
      link: ROUTES.ENTERPRISES_PASSES,
      text: t('navMenu.enterprises-passes'),
      icon: ContactMail,
    },
    {
      link: ROUTES.ENTERPRISES_TICKETS,
      text: t('navMenu.enterprises-tickets'),
      icon: RateReviewOutlined,
    },
    {
      text: t('navMenu.enterprises-stuff'),
      link: ROUTES.ENTERPRISES_STUFF,
      icon: ArchiveOutlined,
    },
    {
      link: ROUTES.ENTERPRISES_EMPLOYEE,
      text: t('navMenu.enterprises-employee'),
      icon: ContactsOutlined,
    },
    {
      text: t('navMenu.enterprises-contacts'),
      link: ROUTES.ENTERPRISES_CONTACTS,
      icon: Phone,
    },
    {
      link: ROUTES.ENTERPRISES_COMPANY,
      text: t('navMenu.enterprises-company'),
      icon: Business,
    },
    {
      text: t('navMenu.enterprises-objects'),
      link: ROUTES.ENTERPRISES_OBJECTS,
      icon: ViewQuiltOutlined,
    },
    {
      link: ROUTES.ENTERPRISES_PARKING,
      text: t('navMenu.enterprises-parking'),
      icon: LocalParking,
    },
    {
      link: ROUTES.ENTERPRISES_VEHICLES,
      text: t('navMenu.enterprises-vehicles'),
      icon: DirectionsCar,
    },
    {
      link: ROUTES.ENTERPRISES_NEWS,
      text: t('navMenu.enterprises-news'),
      icon: Info,
    },
    {
      link: ROUTES.ENTERPRISES_EVENTS,
      text: t('navMenu.enterprises-events'),
      icon: EventNoteOutlined,
    },
  ],
};

export const visibilityConfig = [
  /* MANAGEMENT */
  ROUTES.DASHBOARD,
  ROUTES.DASHBOARD_CLIMATE,
  ROUTES.USERS,
  ROUTES.ROLES,
  ROUTES.COMPANY,
  ROUTES.NEWS,
  ROUTES.OBJECTS_NEW,
  ROUTES.ENTERPRISES,
  ROUTES.ENTERPRISE_LIST,
  ROUTES.COUNTERS,
  ROUTES.TICKETS_DISPLAY,
  ROUTES.TICKETS,
  ROUTES.INTERCOMS,
  ROUTES.ALARMS,
  ROUTES.CCTV,
  ROUTES.GUESTS,
  ROUTES.APPOINTMENT,
  ROUTES.BARRIERS,
  ROUTES.BARRIERS_NEW,
  ROUTES.VEHICLES,
  ROUTES.CALLS,
  ROUTES.CONNECTIONS,
  ROUTES.USER_APP,
  ROUTES.SMS_GATEWAY_SETTINGS,
  ROUTES.JOB_SCHEDULE,
  ROUTES.TBO_SCHEDULE,
  ROUTES.CONCIERGE,
  ROUTES.PASSES,
  ROUTES.QUARANTINE,
  ROUTES.DOCUMENT_FLOW,
  ROUTES.STATISTICS,
  ROUTES.DEBTORS_LIST,
  ROUTES.DEBTORS_NOTIFICATIONS,
  ROUTES.DEBTORS_RECEIPTS,
  ROUTES.DEBTORS_SETTINGS,
  ROUTES.DEBTORS_ACCESS,
  ROUTES.MARKET_COMPANIES,
  ROUTES.MARKET_OFFERS,
  ROUTES.MESSAGES,
  ROUTES.APARTMENT_ACCEPTANCE,
  ROUTES.PARKING,
  ROUTES.PARKING_LOTS,
  ROUTES.EVENTS_LOG,
  ROUTES.POLLS,
  ROUTES.STUFF,
  ROUTES.ENTERPRISE_CONTRACTOR,
  ROUTES.SECURITY,
  ROUTES.CONTAINERS,
  ROUTES.DOORS,
  ROUTES.SCHEDULE,
  ROUTES.ACCESS_KEYS,
  ROUTES.DASHBOARD,
  ROUTES.EQUIPMENT,
  ROUTES.WORKS,
  ROUTES.EVENT_SERVICE,
  ROUTES.TICKETS_TYPES,
  ROUTES.DEVICES_HINTS,
  ROUTES.ACCESS_GROUPS,
  ROUTES.REPORTS,
  ROUTES.ACTIVITY,
  ROUTES.COUNTERS_CHECK,
  ROUTES.PLANNED_WORKS,
  ROUTES.USERS_GROUPS,
  ROUTES.ACCESS_LEVEL,
  ROUTES.PASS_CONFIG,
  ROUTES.QUALIFICATIONS,
  ROUTES.PLANNED_WORK_SCHEDULE,
  ROUTES.WORK_FLOWS,
  ROUTES.ACCOUNTS,
  ROUTES.CHECK_LISTS,
  ROUTES.INSPECTIONS,
  ROUTES.CLIENT_PREMISES,
  ROUTES.RENT,
  ROUTES.GUESTS_TYPES,
  ROUTES.STORIES,
  ROUTES.ACCOUNTS_NEW,
  ROUTES.EMPLOYEE_ASSIGNMENT,
  ROUTES.RESIDENTS_NEW,
  ROUTES.TICKETS_FIELDS,
  ROUTES.TICKETS_SCHEMAS,
  ROUTES.PEOPLE,
  ROUTES.APPEALS,
  ROUTES.ANALYTICS,

  /* ENTERPRISES */
  ROUTES.ENTERPRISES_COMPANY,
  ROUTES.ENTERPRISES_EMPLOYEE,
  ROUTES.ENTERPRISES_PASSES,
  ROUTES.ENTERPRISES_CONTACTS,
  ROUTES.ENTERPRISES_VEHICLES,
  ROUTES.ENTERPRISES_TICKETS,
  ROUTES.ENTERPRISES_EVENTS,
  ROUTES.ENTERPRISES_STUFF,
  ROUTES.ENTERPRISES_PARKING,
  ROUTES.ENTERPRISES_OBJECTS,
  ROUTES.ENTERPRISES_NEWS,
];
